<template>
  <FormField :label="label" :optional="optional" :error="showError ? errorMsg : undefined">
    <input
      :value="modelValue"
      type="email"
      inputmode="email"
      autocomplete="off"
      :placeholder="placeholder"
      @input="onInput"
      @blur="touched = true"
    />
  </FormField>
</template>

<script setup lang="ts">
/**
 * Shared email input with inline validation, following the exact PhoneInput
 * pattern (touched-on-blur; error shown only after the field is blurred).
 *
 * - v-model carries the RAW string (never normalised — kept as typed).
 * - Emits `update:valid` with the current validity so the PARENT can gate its
 *   submit button. Validity: blank-when-optional is valid; otherwise the shared
 *   validateEmail() decides. An empty optional email is allowed; a non-empty
 *   bad one blocks save.
 * - The error only RENDERS after blur, but `update:valid` fires immediately so
 *   submit-gating is correct even before the user blurs.
 *
 * Built on the shared FormField chrome, like PhoneInput.
 */
import { computed, ref, watch } from 'vue'
import { validateEmail } from '@/lib/validateEmail'
import FormField from '@/components/FormField.vue'

const props = withDefaults(
  defineProps<{
    modelValue: string
    label?: string
    optional?: boolean
    placeholder?: string
  }>(),
  {
    label: 'Email',
    optional: true,
    placeholder: 'name@example.com',
  },
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
  (e: 'update:valid', valid: boolean): void
}>()

const touched = ref(false)

const isValid = computed(() => {
  const v = (props.modelValue ?? '').trim()
  if (!v) return props.optional
  return validateEmail(v).ok
})

const errorMsg = computed(() => {
  const v = (props.modelValue ?? '').trim()
  if (!v) return props.optional ? '' : 'Email is required'
  return validateEmail(v).error ?? ''
})

const showError = computed(() => touched.value && !isValid.value && !!errorMsg.value)

function onInput(e: Event) {
  emit('update:modelValue', (e.target as HTMLInputElement).value)
}

// Keep the parent's gate in sync. immediate so initial state is reported.
watch(isValid, (v) => emit('update:valid', v), { immediate: true })
</script>
